import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateOrdinal } from './date_util';
import { ExcelButton } from './excel_button';

const ITEMS_PER_PAGE = 50;

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

export const Invoices = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user.currentUser);
  const logoutUser = () => {
    dispatch({ type: 'LOG_OUT', payload: null });
    sessionStorage.setItem('currentUser', null);
    window.location = "/";
  };
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);

  useEffect(() => {
    if (!statusFilterOpen) return;
    const handler = (e) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(e.target)) {
        setStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [statusFilterOpen]);

  const STATUS_OPTIONS = [
    { value: 'All', label: 'All statuses', color: null },
    { value: 'Paid', label: 'Paid', color: 'limegreen' },
    { value: 'Payment Pending', label: 'Payment Pending', color: 'orange' },
    { value: 'Past Due', label: 'Past Due', color: 'crimson' },
  ];

  const getInvoiceStatus = (invoice) => {
    const paid = invoice && invoice[122] ? invoice[122].value : false;
    if (paid) return { text: 'Paid', color: 'limegreen' };
    const dueRaw = invoice && invoice[9] ? invoice[9].value : null;
    if (dueRaw) {
      const due = new Date(dueRaw);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!isNaN(due.getTime()) && due < today) {
        return { text: 'Past Due', color: 'crimson' };
      }
    }
    return { text: 'Payment Pending', color: 'orange' };
  };

  const STATUS_PRIORITY = { 'Past Due': 1, 'Payment Pending': 2, 'Paid': 3 };
  const sortInvoices = (data) => {
    return [...data].sort((a, b) => {
      const statusA = STATUS_PRIORITY[getInvoiceStatus(a).text] || 99;
      const statusB = STATUS_PRIORITY[getInvoiceStatus(b).text] || 99;
      if (statusA !== statusB) return statusA - statusB;
      const dateA = a && a[73] ? new Date(a[73].value) : new Date(0);
      const dateB = b && b[73] ? new Date(b[73].value) : new Date(0);
      return dateB - dateA;
    });
  };

  const filterInvoices = (data, term, status) => {
    return data.filter(item => {
      if (status && status !== 'All' && getInvoiceStatus(item).text !== status) return false;
      if (term) {
        const invoiceNumber = item && item[6] ? String(item[6].value) : "";
        const jobRequester = item && item[423] ? item[423].value : "";
        if (!invoiceNumber.toLowerCase().includes(term) && !jobRequester.toLowerCase().includes(term)) return false;
      }
      return true;
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchInitial = async () => {
      setIsLoading(true);
      const response = await fetch("/apiinvoices?limit=50", { credentials: 'include' });
      const data = await response.json();
      if (data && data.sessionExpired) { logoutUser(); return; }
      if (!isMounted) return;
      const sorted = sortInvoices(data);
      setInvoices(sorted);
      setFilteredInvoices(sorted);
      setIsLoading(false);

      // Phase 2: fetch all records in background
      const allResponse = await fetch("/apiinvoices", { credentials: 'include' });
      const allData = await allResponse.json();
      if (allData && allData.sessionExpired) { logoutUser(); return; }
      if (!isMounted) return;
      const allSorted = sortInvoices(allData);
      setInvoices(allSorted);
      setFilteredInvoices(filterInvoices(allSorted, searchTerm, statusFilter));
      setIsFullyLoaded(true);
    };

    fetchInitial();
    return () => { isMounted = false; };
  }, []);

  const handleChangeInput = (e) => {
    const term = (e.target.value).toLowerCase();
    setSearchTerm(term);
    setFilteredInvoices(filterInvoices(invoices, term, statusFilter));
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    const status = e.target.value;
    setStatusFilter(status);
    setFilteredInvoices(filterInvoices(invoices, searchTerm, status));
    setCurrentPage(1);
  };

  const redirectToInvoicePage = (e, invoiceId) => {
    navigate(`/invoice/${invoiceId}`, {state: {id: invoiceId}});
  };

  const formatDate = formatDateOrdinal;

  const formatCurrency = (value) => {
    if (!value || isNaN(parseFloat(value))) return "$0.00";
    return `$${parseFloat(value).toFixed(2)}`;
  };

  // Check if admin user
  const isAdmin = currentUser && currentUser.role === 'admin';

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Clamp current page if filtered results change
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredInvoices, currentPage]);
//6, 7, 31, 14, 15, 122, 135
  const headers = (
    <tr>
      <th>Invoice Number</th>
      <th>Invoice Date</th>
      <th>Due Date</th>
      <th>Paid Status</th>
      <th>First Shift Start</th>
      <th>Last Shift Start</th>
      <th>Total with Tax</th>
    </tr>
  );

  const exportRows = () => filteredInvoices.map(inv => {
    const status = getInvoiceStatus(inv).text;
    return {
      'Invoice #': inv && inv[6] ? inv[6].value : '',
      'Invoice Date': inv && inv[7] ? formatDate(inv[7].value) : '',
      'Due Date': inv && inv[31] ? formatDate(inv[31].value) : '',
      'Status': status,
      'First Shift Start': inv && inv[14] ? formatDate(inv[14].value) : '',
      'Last Shift Start': inv && inv[15] ? formatDate(inv[15].value) : '',
      'Total': inv && inv[135] ? inv[135].value : '',
    };
  });

  return (
    <div className="tickets">
    <div className="ticketsList">
      <h2 style={{ margin: 0 }}>Invoices</h2>
      {!isAdmin && (
        <>
          <div className="ticketsActionsWrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'stretch', gap: '12px', flex: '1 1 280px', flexWrap: 'wrap' }}>
              <div className="searchInputWrapper inputWrapper" style={{ flex: '1 1 280px', marginBottom: 0 }}>
                <input placeholder="Search invoices..." onChange={handleChangeInput}></input>
              </div>
              <div style={{ position: 'relative' }} ref={statusFilterRef}>
              <button
                type="button"
                onClick={() => setStatusFilterOpen(o => !o)}
                style={{ background: '#B7966A', color: 'white', border: 'none', borderRadius: '9px', padding: '13px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: 'bold', whiteSpace: 'nowrap'}}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                </svg>
                Filter by Status
              </button>
              {statusFilterOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'white', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '220px', zIndex: 10, padding: '6px 0' }}>
                  {STATUS_OPTIONS.map(opt => {
                    const isSelected = statusFilter === opt.value;
                    return (
                      <div
                        key={opt.value}
                        onClick={() => { handleStatusFilterChange({ target: { value: opt.value } }); setStatusFilterOpen(false); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 14px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: isSelected ? 'bold' : 'normal',
                          background: isSelected ? '#f0f4fa' : 'white',
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f7f7f7'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'white'; }}
                      >
                        {opt.color
                          ? <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: opt.color, flexShrink: 0 }}></span>
                          : <span style={{ width: '14px', height: '14px', flexShrink: 0 }}></span>}
                        <span>{opt.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
            </div>
            <ExcelButton rows={exportRows} fileName="invoices.xlsx" sheetName="Invoices" />
          </div>
          {isLoading ? (
            <div className="skeleton-wrapper">
              <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
                Loading invoices...
              </p>
              <table className="skeleton-table">
                <tbody>
                  {headers}
                  {Array.from({ length: 15 }).map((_, idx) => (
                    <tr key={idx}>
                      <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                      <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                      <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                      <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                      <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                      <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                      <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
                Showing {filteredInvoices.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredInvoices.length)} of {filteredInvoices.length} invoices
                {!isFullyLoaded && <span> (loading more...)</span>}
              </p>
              <table id="tableWrapper">
              <tbody>
              {headers}
                {paginatedInvoices && paginatedInvoices.length > 0 && paginatedInvoices.map((invoice, idx) => {
                  const invoiceId = invoice && invoice[3] ? invoice[3].value : null;
                  const status = getInvoiceStatus(invoice);

                  return (
                    <tr key={idx} onClick={(e) => redirectToInvoicePage(e, invoiceId)} className="tableRow">
                      <td className="tableCell" style={{whiteSpace: 'nowrap'}}>{invoice && invoice[6] ? invoice[6].value : ""}</td>
                      <td className="tableCell" style={{whiteSpace: 'nowrap'}}>{invoice && invoice[7] ? formatDate(invoice[7].value) : ""}</td>
                      <td className="tableCell" style={{whiteSpace: 'nowrap'}}>{invoice && invoice[31] ? formatDate(invoice[31].value) : ""}</td>
                      <td className="tableCell" style={{whiteSpace: 'nowrap'}}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: status.color, flexShrink: 0 }}></span>
                          {status.text}
                        </span>
                      </td>
                      <td className="tableCell" style={{whiteSpace: 'nowrap'}}>{invoice && invoice[14] ? formatDate(invoice[14].value) : ""}</td>
                      <td className="tableCell" style={{whiteSpace: 'nowrap'}}>{invoice && invoice[15] ? formatDate(invoice[15].value) : ""}</td>
                      <td className="tableCell" style={{whiteSpace: 'nowrap'}}>{invoice && invoice[135] ? formatCurrency(invoice[135].value) : "$0.00"}</td>
                    </tr>
                  )
                })}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="pagination-wrapper">
                  <button
                    className={`pagination-btn ${currentPage === 1 ? 'pagination-btn--disabled' : ''}`}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  {getPageNumbers(currentPage, totalPages).map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
                    ) : (
                      <button
                        key={page}
                        className={`pagination-btn ${currentPage === page ? 'pagination-btn--active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    )
                  ))}
                  <button
                    className={`pagination-btn ${currentPage === totalPages ? 'pagination-btn--disabled' : ''}`}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
      {isAdmin && (
        <p style={{color: '#666', fontSize: '16px', marginTop: '20px'}}>
          Invoices are not fetched for admin users. Please log in as a client to view invoices.
        </p>
      )}
  </div>
    </div>
    )
}
